import React, { useRef, useState } from 'react';
import { Pressable, Text, StyleSheet } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { uiFont } from '../../styles/agapellaFonts';
import type { ThemeColors } from '../../context/ThemeContext';
import { MESSAGES_HIT, useMessagesSurface } from './messagesSurface';

/**
 * CORE-311 — voice message in a mobile-web chat: play / pause and the time.
 *
 * The browser's own `<audio controls>` (kept on desktop) brings a progress
 * slider, a mute button and an overflow menu — clutter in a phone bubble, and
 * there is no reliable way to switch them off one by one: Chrome and Safari
 * draw their controls differently. So the element stays hidden and only plays.
 *
 * Web only: rendered from a `Platform.OS === 'web'` branch of the thread.
 */
export function VoiceMessagePlayer({ url, durationSec, mine, tc }: {
  url: string;
  /** Recorded length, when the attachment carries it. */
  durationSec?: number;
  mine?: boolean;
  tc: ThemeColors;
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  // MediaRecorder WebM reports `Infinity` until played through — keep the
  // recorded length until the element knows a real one.
  const [total, setTotal] = useState(finiteSeconds(durationSec));

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) void el.play().catch(() => setPlaying(false));
    else el.pause();
  };

  const color = mine ? '#fff' : tc.textPrimary;
  const inMessages = !!useMessagesSurface();
  return (
    <Pressable
      onPress={toggle}
      style={[
        styles.root,
        { backgroundColor: mine ? 'rgba(0,0,0,0.18)' : 'rgba(127,127,127,0.16)' },
        // CORE-1791 — в разделе сообщений кнопка голосового не ниже 44 (было 43).
        inMessages && { minHeight: MESSAGES_HIT },
      ]}
      accessibilityRole="button"
      accessibilityLabel={playing ? 'Пауза' : 'Слушать голосовое'}
      testID="chat-voice-player"
    >
      <Ionicons name={playing ? 'pause' : 'play'} size={20} color={color} />
      <Text style={[styles.time, { color }]}>
        {formatSeconds(current)}{total ? ` / ${formatSeconds(total)}` : ''}
      </Text>
      <audio
        ref={audioRef}
        preload="none"
        src={url}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); setCurrent(0); }}
        onTimeUpdate={(e) => setCurrent(e.currentTarget.currentTime)}
        onDurationChange={(e) => {
          const d = finiteSeconds(e.currentTarget.duration);
          if (d) setTotal(d);
        }}
        style={{ display: 'none' }}
      />
    </Pressable>
  );
}

function finiteSeconds(value: number | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0;
}

function formatSeconds(sec: number): string {
  const whole = Math.floor(sec);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
}

const styles = StyleSheet.create({
  root: {
    flexDirection: 'row', alignItems: 'center', gap: 10, alignSelf: 'flex-start',
    minWidth: 120, paddingVertical: 10, paddingLeft: 12, paddingRight: 16,
    borderRadius: 22, marginBottom: 4, cursor: 'pointer' as any,
  },
  time: { ...uiFont(500, 600), fontSize: 15, lineHeight: 20, fontVariant: ['tabular-nums'] },
});
